import useApi from '@/api'
import { BuildListProfile, CraftingProfile, getProfilePath } from '@/common'
import Data from '@/data'
import {
  folderDelete,
  folderIsChild,
  folderRename,
  folderSetParent,
  profileDelete,
  profileMove,
  profileRename,
  profileUpdate,
  validateFolders
} from '@/logic/profiles'
import { stateDeleteImports, stateSetImported } from '@/logic/stateReducers'
import Store from '@/store'
import EditingContext, { EditingHandler } from '@/store/editingContext'
import Session from '@/store/session'
import { ProfileType, UserFolders, UserProfile } from '@/types/api'
import { DragItem } from '@/types/dragDrop'
import Resizable from '@/ui/resizable'
import Tooltip from '@/ui/tooltip'
import { OverlayContext } from '@/ui/utils/OverlayManager'
import { VirtualList } from '@/ui/virtual'
import { chainedRef } from '@/utils'
import {
  faCaretRight,
  faFolder,
  faFolderOpen,
  faFolderPlus,
  faPencilAlt,
  faSave,
  faSpinner,
  faTrashAlt
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome'
import { DndProviderWrapper } from '@maxroll-media-group/common-libs'
import classNames from 'classnames'
import React, { ReactNode } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import scrollIntoView from 'scroll-into-view-if-needed'
import TextEdit from '../TextEdit'
import { useImportId } from '../importTracker'
import { ClassIcon } from '../planner/utils/images'
import { defaultFilter } from './ImportPane'
import { entryFromExport, exportProfile } from './common'
import { publicFromProfile } from './public-section'
import useLoadProfile from './useLoadProfile'

const ProfileLink = React.forwardRef(function ProfileLink(
  {
    id,
    onClick,
    children,
    type,
    ...props
  }: {
    id: string
    onClick: (id: string) => void
    children: ReactNode
    type: ProfileType
  },
  ref: React.ForwardedRef<HTMLAnchorElement>
) {
  const url: any = new URL(getProfilePath(id, type), window.location.href)
  const clickHandler = React.useCallback(
    (e: any) => {
      if (e.ctrlKey || e.shiftKey || e.altKey) return
      e.preventDefault()
      onClick(id)
    },
    [id, onClick]
  )
  return (
    <a href={url} onClick={clickHandler} {...props} ref={ref}>
      {children}
    </a>
  )
})

type SessionDispatch = ReturnType<typeof Session.useDispatch>

function ProfileFolderCreate({ dispatch, folders }: { dispatch: SessionDispatch; folders: UserFolders }) {
  const editDispatch = EditingContext.useDispatch()

  const onClick = React.useCallback(() => {
    let id = 1
    while (folders[id]) {
      id += 1
    }
    dispatch.setFolders((folders) => ({
      ...folders,
      [id]: { name: `New Folder`, parent: 0 }
    }))
    editDispatch({ type: 'folder', id })
  }, [dispatch, editDispatch, folders])

  return (
    <li>
      <span className="poe-create" onClick={onClick}>
        <FontAwesomeIcon icon={faFolderPlus} /> {`New Folder`}
      </span>
    </li>
  )
}

const ProfileFolderDelete = React.memo(function ProfileFolderDelete({
  id,
  setActive,
  dispatch
}: {
  id: number
  setActive: (id: number) => void
  dispatch: SessionDispatch
}) {
  const sessionStore = Session.useStore()
  const overlays = React.useContext(OverlayContext)
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const mainDispatch = Store.useDispatch()

  const onClick = React.useCallback(
    async (e: React.MouseEvent) => {
      e.persist()
      const { profiles, folders } = sessionStore.getState()
      const parent = folders?.[id]?.parent || 0
      if (!profiles || profiles.find((p) => p.folder === id)) {
        const result = await overlays.menu(e, [`Move all profiles to parent folder`, `Delete all profiles`])
        try {
          if (result === 0) {
            await api.folders('moveall', { from: id, to: parent })
            dispatch.setProfiles((profiles) => profiles.map((p) => (p.folder === id ? { ...p, folder: parent } : p)))
          } else if (result === 1) {
            await api.folders('delall', { from: id })
            dispatch.setProfiles((profiles) => profiles.filter((p) => p.folder !== id))
            if (profiles) {
              mainDispatch.setState((state) =>
                stateDeleteImports(state, ...profiles.filter((p) => p.folder === id).map((p) => p.id))
              )
            }
          } else {
            return
          }
        } catch (error) {
          setError((error as Error).message)
          return
        }
      }

      dispatch.setFolders((folders) => folderDelete(folders, id))
      setActive(parent)
    },
    [sessionStore, overlays, setError, api, dispatch, mainDispatch, setActive, id]
  )

  return (
    <span className="poe-modify" onClick={onClick}>
      <FontAwesomeIcon icon={faTrashAlt} />
    </span>
  )
})

type Subfolders = { [id in number]?: number[] }
type Counts = { [id in number]?: number }

function ProfileFolder({
  id,
  ...props
}: {
  id: number
  folders: UserFolders
  subfolders: Subfolders
  counts: Counts
  active: number
  setActive: (id: number) => void
  dispatch: SessionDispatch
}) {
  const { folders, subfolders, counts, active, setActive, dispatch } = props
  const [editing, setEditing] = React.useState(false)
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')

  const isActive = active === id
  const onClick = React.useCallback(() => setActive(id), [setActive, id])

  const api = useApi()

  const [{ dragging }, drag] = useDrag({
    type: 'FOLDER',
    item: { type: 'FOLDER', id },
    canDrag: () => id !== 0 && !editing,
    collect: (monitor) => ({ dragging: monitor.isDragging() })
  })
  const [{ draggingSomething, dropping }, drop] = useDrop({
    accept: ['FOLDER', 'PROFILE'],
    canDrop(item: DragItem) {
      if (item.type === 'FOLDER') {
        return item.id !== 0 && folders[item.id].parent !== id && !folderIsChild(folders, id, item.id)
      } else if (item.type === 'PROFILE') {
        return item.folder !== id
      } else {
        return false
      }
    },
    drop(item: DragItem) {
      if (item.type === 'FOLDER') {
        dispatch.setFolders((folders) => folderSetParent(folders, item.id, id))
      } else if (item.type === 'PROFILE') {
        dispatch.setProfiles((profiles) => profileMove(profiles, item.id, id))
        setStatus(`Moving...`)
        setError(undefined)
        api
          .folders('move', { id: item.id, to: id })
          .catch((error) => {
            dispatch.setProfiles((profiles) => profileMove(profiles, item.id, item.folder))
            setError(error.message)
          })
          .finally(() => setStatus(undefined))
      }
    },
    collect: (monitor) => ({
      dropping: monitor.canDrop() && monitor.isOver(),
      draggingSomething: ['FOLDER', 'PROFILE'].includes(monitor.getItemType() as string)
    })
  })

  const onEdit = React.useCallback((e: React.UIEvent) => {
    e.stopPropagation()
    setEditing(true)
  }, [])
  const onEditChange = React.useCallback(
    (name: string | undefined) => {
      setEditing(false)
      if (name) {
        dispatch.setFolders((folders) => folderRename(folders, id, name))
      }
    },
    [dispatch, id]
  )

  const lineRef = React.useRef<HTMLLIElement>(null)
  const editingCallback = React.useCallback<EditingHandler>(
    (task) => {
      if (task.type === 'folder' && task.id === id) {
        scrollIntoView(lineRef.current!, {
          scrollMode: 'if-needed',
          block: 'center'
        })
        setEditing(true)
        return true
      }
    },
    [id]
  )
  EditingContext.useHandler(editingCallback)

  const nodeRef = React.useMemo(() => chainedRef(drag, drop), [drag, drop])

  const highlight = dropping || (isActive && !draggingSomething)

  return (
    <li ref={lineRef}>
      <div
        className={classNames({
          'poe-active': highlight,
          'poe-dragging': dragging,
          'poe-root': id === 0,
          'poe-empty': counts && !counts[id]
        })}>
        <span
          className="poe-label"
          onClick={isActive ? undefined : onClick}
          ref={nodeRef}
          title={counts ? `${counts[id] || 0} profiles` : undefined}>
          <span className="poe-icon">
            <FontAwesomeIcon icon={isActive ? faFolderOpen : faFolder} />
          </span>
          {id ? (
            editing ? (
              <TextEdit value={folders[id].name} onChange={onEditChange} />
            ) : (
              folders[id].name
            )
          ) : (
            `My Profiles`
          )}
        </span>
        {!!(id !== 0 && !draggingSomething && !editing) && (
          <span className="poe-modify" onClick={onEdit}>
            <FontAwesomeIcon icon={faPencilAlt} />
          </span>
        )}
        {!!(id !== 0 && !draggingSomething && !editing) && (
          <ProfileFolderDelete id={id} setActive={setActive} dispatch={dispatch} />
        )}
      </div>
      {!!subfolders[id] && (
        <ul>
          {id === 0 && <ProfileFolderCreate dispatch={dispatch} folders={folders} />}
          {subfolders[id]!.map((sub: any) => (
            <ProfileFolder key={sub} id={sub} {...props} />
          ))}
        </ul>
      )}
    </li>
  )
}

const ProfileFolders = React.memo(function ProfileFolders({
  folders,
  profiles,
  ...props
}: {
  folders: UserFolders
  profiles: UserProfile[]
  active: number
  setActive: (id: number) => void
  dispatch: SessionDispatch
}) {
  const subfolders = React.useMemo(() => {
    const subfolders: Subfolders = {}
    subfolders[0] = []
    for (const id in folders) {
      const parent = folders[id].parent
      subfolders[parent] = subfolders[parent] ?? []
      subfolders[parent]!.push(parseInt(id))
    }
    for (const id in subfolders) {
      subfolders[id]!.sort((a: any, b: any) => folders[a].name.localeCompare(folders[b].name))
    }
    return subfolders
  }, [folders])
  const counts = React.useMemo(() => {
    const counts: Counts = {}
    if (!profiles) return counts
    for (const profile of profiles) {
      counts[profile.folder] = (counts[profile.folder] ?? 0) + 1
    }
    return counts
  }, [profiles])
  if (!subfolders[0]) return null
  return <ProfileFolder id={0} folders={folders} subfolders={subfolders} counts={counts} {...props} />
})

function ProfileLine({
  hero,
  active,
  onLoad,
  onSave,
  onDelete,
  onRename,
  type
}: {
  hero: UserProfile
  active: boolean
  onLoad: (id: string) => void
  onSave: (id: string, name?: string, saveMode?: 'all' | 'current') => void
  onDelete: (profile: UserProfile) => void
  onRename: (profile: UserProfile, name: string) => void
  type: ProfileType
}) {
  const overlays = React.useContext(OverlayContext)
  const [renaming, setRenaming] = React.useState(false)
  const setStatus = EditingContext.useStateSetter('profilesStatus')

  const [, drag] = useDrag({
    type: 'PROFILE',
    item: { type: 'PROFILE', id: hero.id, folder: hero.folder }
  })

  const onSaveClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      overlays.menu(e, [`Overwrite?`]).then((result) => {
        if (result === 0) onSave(hero.id, hero.name)
      })
    },
    [overlays, hero, onSave]
  )

  const onRenameClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      setStatus(
        `Due to technical limitations, renaming a profile only changes its name in the list - you need to save over it to update the page title.`
      )
      setRenaming(true)
    },
    [setStatus]
  )
  const onRenameChange = React.useCallback(
    (name: string | undefined) => {
      setRenaming(false)
      setStatus(undefined)
      if (name !== hero.name && name != null) {
        onRename(hero, name)
      }
    },
    [hero, onRename, setStatus]
  )

  const onDeleteClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      overlays.menu(e, [`Confirm delete?`]).then((result) => {
        if (result === 0) onDelete(hero)
      })
    },
    [overlays, hero, onDelete]
  )

  const charClass = hero.class ? Data.ascendancyClass[hero.class] ?? hero.class : undefined
  const classData = charClass ? Data.characters[charClass] : undefined
  const ascendancyData = hero.class ? classData?.ascendancies[hero.class] : undefined

  return (
    <li
      className={classNames({
        'poe-active': active,
        'poe-renaming': renaming,
        'poe-public': hero.public,
        'poe2-convert': type === 'planner' && hero.type !== 'planner'
      })}>
      <ProfileLink id={hero.id} onClick={onLoad} ref={renaming ? undefined : drag} type={type}>
        {(type === 'planner' || type === 'pob') && (
          <ClassIcon name={ascendancyData?.name ?? classData?.name ?? ''} size={24} className="poe-class-icon" />
        )}
        <span
          className={classNames('poe-profile-name', {
            'poe-unnamed': !hero.name
          })}>
          {renaming ? <TextEdit value={hero.name || ''} onChange={onRenameChange} /> : hero.name || `Unnamed profile`}
        </span>
      </ProfileLink>
      <span className="poe-profile-saved">
        <span className="poe-modify">
          <span onClick={onRenameClick} title={`Rename profile`}>
            <FontAwesomeIcon icon={faPencilAlt} />
          </span>
          <span onClick={onSaveClick} title={`Update profile`}>
            <FontAwesomeIcon icon={faSave} />
          </span>
          <span onClick={onDeleteClick} title={`Delete profile`}>
            <FontAwesomeIcon icon={faTrashAlt} />
          </span>
        </span>
        <span className="poe-date">{new Date(hero.date).toLocaleString()}</span>
      </span>
    </li>
  )
}

function useUpdateProfile(type: ProfileType) {
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const mainStore = Store.useStore()
  const mainDispatch = Store.useDispatch()
  const sessionStore = Session.useStore()
  const sessionDispatch = Session.useDispatch()
  const user = Session.useSelector((state) => state.user)
  return React.useCallback(
    (id: string, name?: string) => {
      let state = mainStore.getState()
      if (state.imported?.user && state.imported.user.id !== user?.id && state.planner?.author) {
        state = { ...state }
        state.planner = { ...state.planner! }
        delete state.planner.author
        mainDispatch.setState(state)
      }
      const imported = state.imported
      const publicOptions = imported?.public ? publicFromProfile(imported, user) : undefined
      const data = exportProfile(state, name ?? imported?.name, publicOptions, type)
      data.id = id

      const profile = sessionStore.getState().profiles?.find((p) => p.id === id)
      if (profile) {
        data.folder = profile.folder
      } else if (imported?.id === id) {
        data.folder = imported.folder
      }

      const profileEntry = entryFromExport(data, id, user, imported)

      setError(undefined)
      setStatus(`Saving...`)

      if (profile) {
        sessionDispatch.setProfiles((profiles) => profileUpdate(profiles, profileEntry))
      }

      return api.save(data).then(
        (id) => {
          const url = new URL(getProfilePath(id, type), window.location.href)
          setStatus(url.href)
          mainDispatch.setState((state) => stateSetImported(state, profileEntry))
        },
        (error: Error) => {
          // restore profile
          if (profile) {
            sessionDispatch.setProfiles((profiles) => {
              const index = profiles?.findIndex((p) => p.id === id)
              if (!profiles || index < 0) return profiles
              profiles = profiles.slice()
              profiles[index] = profile
              return profiles
            })
          }
          setStatus(undefined)
          setError(error.message)
        }
      )
    },
    [api, mainStore, setStatus, setError, sessionStore, sessionDispatch, mainDispatch, user, type]
  )
}

export const ProfileUpdateButton = React.memo(function ProfileUpdateButton({
  type,
  ...props
}: { type: ProfileType } & Omit<FontAwesomeIconProps, 'icon'>) {
  const userId = Session.useSelector((state) => state.user?.id)
  const saveStateId = Store.useSelector((state) =>
    userId &&
    state.imported &&
    state.imported.modified &&
    (state.imported.user?.id === userId || state.imported.id === CraftingProfile) &&
    state.imported.id !== BuildListProfile
      ? state.imported.id
      : undefined
  )

  const setSpinner = EditingContext.useStateSetter('spinner')

  const onSave = useUpdateProfile(type)
  const onClick = React.useCallback(() => {
    if (saveStateId) {
      setSpinner((spin) => spin + 1)
      onSave(saveStateId).finally(() => setSpinner((spin) => spin - 1))
    }
  }, [onSave, saveStateId, setSpinner])

  if (!saveStateId) return null
  return (
    <Tooltip tooltip={`Update profile`}>
      <FontAwesomeIcon icon={faSave} onClick={onClick} {...props} />
    </Tooltip>
  )
})

const ProfileList = React.memo(function ProfileList({
  profiles,
  folder,
  type
}: {
  profiles: UserProfile[] | undefined
  folder: number
  type: ProfileType
}) {
  const setStatus = EditingContext.useStateSetter('profilesStatus')
  const setError = EditingContext.useStateSetter('profilesError')
  const api = useApi()
  const dispatch = Session.useDispatch()
  const mainDispatch = Store.useDispatch()
  const currentProfile = useImportId()

  const loadProfile = useLoadProfile(type)

  const onSave = useUpdateProfile(type)

  const onDelete = React.useCallback(
    (profile: UserProfile) => {
      dispatch.setProfiles((profiles) => profileDelete(profiles, profile.id))
      setError(undefined)
      setStatus(`Deleting...`)
      api
        .delete(profile.id)
        .then(
          () => {
            mainDispatch.setState((state) => stateDeleteImports(state, profile.id))
          },
          (error: Error) => {
            dispatch.setProfiles((profiles) => profiles && [...profiles, profile])
            setError(error.message)
          }
        )
        .finally(() => setStatus(undefined))
    },
    [api, dispatch, mainDispatch, setStatus, setError]
  )

  const onRename = React.useCallback(
    (profile: UserProfile, name: string) => {
      dispatch.setProfiles((profiles) => profileRename(profiles, profile.id, name))
      setError(undefined)
      setStatus(`Renaming...`)
      api
        .folders('rename', { id: profile.id, name })
        .catch((error: Error) => {
          dispatch.setProfiles((profiles) => profileRename(profiles, profile.id, profile.name))
          setError(error.message)
        })
        .finally(() => setStatus(undefined))
    },
    [api, dispatch, setStatus, setError]
  )

  const filtered = React.useMemo(() => {
    return (
      profiles && profiles.filter((profile) => folder < 0 || profile.folder === folder).sort((a, b) => b.date - a.date)
    )
  }, [profiles, folder])

  const renderProfile = React.useCallback(
    (profile: UserProfile) => (
      <ProfileLine
        key={profile.id}
        hero={profile}
        active={currentProfile === profile.id}
        onLoad={loadProfile}
        onSave={onSave}
        onDelete={onDelete}
        onRename={onRename}
        type={type}
      />
    ),
    [currentProfile, loadProfile, onDelete, onSave, onRename, type]
  )

  if (!profiles) {
    return (
      <div className="poe-profiles">
        {!profiles && (
          <li className="poe-loading">
            <FontAwesomeIcon icon={faSpinner} spin /> {`Loading...`}
          </li>
        )}
      </div>
    )
  }

  return (
    <VirtualList
      className="poe-profiles"
      items={filtered!}
      renderItem={renderProfile}
      maxHeight={400}
      itemHeight={20}
    />
  )
})

function ProfilesView({ type, filter }: { type: ProfileType; filter: (profile: UserProfile) => any }) {
  const [status, setStatus] = EditingContext.useState('profilesStatus')
  const [error, setError] = EditingContext.useState('profilesError')
  const [currentFolder, setCurrentFolder] = EditingContext.useState('profilesCurrentFolder')
  const folders = Session.useSelector((store) => store.folders)
  const profiles = Session.useSelector((store) => store.profiles)
  const dispatch = Session.useDispatch()

  const [showFolders, setShowFolders] = EditingContext.useState('profilesShowFolders')
  const timeoutRef = React.useRef<number>()
  const onToggleFolders = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = undefined
    }
    if (showFolders === 'collapsed') {
      setShowFolders('expanding')
      timeoutRef.current = window.setTimeout(() => setShowFolders('expanded'), 250)
    } else {
      setShowFolders('collapsed')
    }
  }, [showFolders, setShowFolders])

  const api = useApi()

  // load/save folders hook
  const foldersPrev = React.useRef<any>(false) // we use a value that can't be equal to any actual state
  React.useEffect(() => {
    if (foldersPrev.current === false) {
      if (!folders) {
        // initial load
        api.folders().then(
          (result) => {
            foldersPrev.current = result
            dispatch.setFolders(validateFolders(result))
          },
          (error) => setError(error.message)
        )
      }
    } else if (foldersPrev.current !== folders && foldersPrev.current !== null) {
      if (!folders) {
        // we already loaded, but dispatch didn't go through yet
        return
      }
      // folders changed through user action
      setStatus(`Saving...`)
      setError(undefined)
      api
        .folders('save', folders)
        .catch((error) => setError(error.message))
        .finally(() => setStatus(undefined))
    }
    foldersPrev.current = folders
  }, [api, dispatch, folders, setStatus, setError])

  // load profiles hook
  const profilesLoaded = React.useRef(false)
  React.useEffect(() => {
    if (profilesLoaded.current) return
    profilesLoaded.current = true
    if (!profiles) {
      setStatus(`Loading...`)
      setError(undefined)
      api
        .profiles()
        .then(
          (result) => dispatch.setProfiles(result),
          (error: Error) => setError(error.message)
        )
        .finally(() => setStatus(undefined))
    }
  }, [api, dispatch, profiles, setStatus, setError])

  const statusRef = React.useRef<HTMLTextAreaElement>(null)
  const onStatusFocus = React.useCallback(() => {
    statusRef.current!.select()
  }, [])

  const filtered = React.useMemo(() => profiles?.filter(filter), [filter, profiles])

  return (
    <DndProviderWrapper className="poe-MyProfiles">
      {(folders || filtered) && (
        <div className="poe-profiles-view">
          <Resizable
            className={classNames('poe-folders', {
              'poe-collapsed': showFolders === 'collapsed',
              'poe-expanding': showFolders === 'expanding'
            })}
            side={showFolders === 'expanded' ? 'e' : ''}>
            <ul className="poe-folder-list">
              {!folders && (
                <li className="poe-loading">
                  <FontAwesomeIcon icon={faSpinner} spin /> {`Loading...`}
                </li>
              )}
              {folders && filtered && (
                <ProfileFolders
                  folders={folders}
                  profiles={filtered}
                  active={currentFolder}
                  setActive={setCurrentFolder}
                  dispatch={dispatch}
                />
              )}
            </ul>
            <div className="poe-toggle" onClick={onToggleFolders}>
              <FontAwesomeIcon icon={faCaretRight} />
            </div>
          </Resizable>
          <ProfileList profiles={filtered} folder={showFolders === 'expanded' ? currentFolder : -1} type={type} />
        </div>
      )}
      <div className="poe-status">
        {!!(error || status) && <textarea readOnly value={error || status} ref={statusRef} onFocus={onStatusFocus} />}
      </div>
    </DndProviderWrapper>
  )
}

export default function ProfilesSection({
  type,
  filter = (profile) => defaultFilter(profile, type)
}: {
  type: ProfileType
  filter?: (profile: UserProfile) => any
}) {
  const loggedIn = Session.useSelector((store) => store.user != null)

  if (loggedIn) {
    return <ProfilesView type={type} filter={filter} />
  } else {
    return <div className="poe-SectionContent">Log in using your Maxroll account to track your saved profiles</div>
  }
}
